import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, BadgePercent, Gem, ShieldCheck } from "lucide-react";
import { api, discountOf, fileUrl } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import Marquee from "@/components/Marquee";
import { UniverseCards } from "@/components/CategoryNav";
import { useShop } from "@/context/ShopContext";
import { MaskedLine, Reveal } from "@/components/Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1613521076081-2820f9746a2d?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";

const CHAPTERS = [
  { n: "01", title: "Grandes marques authentiques", text: "Huda Beauty, Rare Beauty, Kayali, Chanel… Chaque produit est sélectionné et vérifié. Zéro contrefaçon, que du vrai." },
  { n: "02", title: "Jusqu'à -50 %", text: "Des prix de référence réels, barrés en toute transparence, et ton économie affichée en euros sur chaque fiche." },
  { n: "03", title: "Le luxe devient accessible", text: "Une sélection courte et pointue, renouvelée en permanence. Les pépites partent vite — très vite." },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const { settings } = useShop() || {};
  const load = () => api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const heroImg = settings?.hero_image ? fileUrl(settings.hero_image) : HERO_IMG;

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const yBadge = useTransform(scrollYProgress, [0, 1], [0, -70]);

  const live = products.filter((p) => !p.coming_soon);
  const soon = products.filter((p) => p.coming_soon);
  const deals = [...live].sort((a, b) => discountOf(b) - discountOf(a)).slice(0, 4);
  const gems = live.filter((p) => p.is_new).length ? live.filter((p) => p.is_new).slice(0, 4) : live.slice(0, 4);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden grain bg-gradient-to-b from-kay-rose/70 via-kay-cream to-kay-cream">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-14 pb-20 lg:pt-20 lg:pb-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 relative z-10">
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.7 }}
              className="font-mono text-[11px] sm:text-xs tracking-[0.35em] uppercase text-kay-pink mb-6"
              data-testid="hero-eyebrow"
            >
              Kay Beauty — Les grandes marques, les petits prix
            </motion.p>
            <h1 className="font-display font-medium tracking-tight leading-[1.02] text-5xl sm:text-7xl lg:text-[5.6rem]" data-testid="hero-title">
              <MaskedLine delay={0.25}>Belle,</MaskedLine>
              <MaskedLine delay={0.4} className="italic text-kay-pink">sans exploser</MaskedLine>
              <MaskedLine delay={0.55}>ton budget.</MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.7 }}
              className="mt-7 max-w-md text-base md:text-lg text-kay-ink/70 leading-relaxed"
              data-testid="hero-subtitle"
            >
              Découvre une sélection de produits authentiques de grandes marques, proposés jusqu'à -50 %.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.7 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/boutique"
                data-testid="hero-cta-offers"
                className="group rounded-full bg-kay-ink text-white pl-7 pr-5 py-3.5 text-sm font-semibold flex items-center gap-3 hover:bg-kay-pink transition-colors"
              >
                Découvrir les offres
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/coming-soon"
                data-testid="hero-cta-coming"
                className="rounded-full border border-kay-ink/20 px-7 py-3.5 text-sm font-semibold hover:border-kay-ink hover:bg-white transition-all"
              >
                Bientôt disponibles
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.2, duration: 0.8 }}
              className="mt-12 flex gap-8 font-mono text-[11px] uppercase tracking-[0.2em] text-kay-ink/50"
            >
              <span data-testid="hero-stat-discount" className="flex items-center gap-2"><BadgePercent size={14} className="text-kay-pink" /> Jusqu'à -50 %</span>
              <span className="flex items-center gap-2"><ShieldCheck size={14} className="text-kay-pink" /> 100 % authentique</span>
              <span className="hidden sm:flex items-center gap-2"><Gem size={14} className="text-kay-pink" /> Pépites vérifiées</span>
            </motion.div>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <motion.div style={{ y: yImg }} className="arch-frame overflow-hidden border border-white shadow-[0_30px_80px_rgba(224,82,151,0.25)]">
                <img src={heroImg} alt="Univers beauté Kay Beauty" className="w-full aspect-[3/4] object-cover" data-testid="hero-image" />
              </motion.div>
              <motion.div style={{ y: yBadge }} className="absolute -top-8 -right-4 sm:-right-8">
                <div className="relative w-28 h-28 sm:w-36 sm:h-36 animate-float">
                  <svg viewBox="0 0 100 100" className="w-full h-full animate-spin-slow">
                    <defs>
                      <path id="circlePath" d="M 50,50 m -38,0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0" />
                    </defs>
                    <text className="font-mono" fontSize="9.5" letterSpacing="2.5" fill="#121212">
                      <textPath href="#circlePath">JUSQU'À -50 % • GRANDES MARQUES •</textPath>
                    </text>
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="font-display italic text-2xl sm:text-3xl text-kay-pink">-50%</span>
                  </div>
                </div>
              </motion.div>
              <div className="absolute -bottom-6 -left-4 sm:-left-10 bg-white/85 backdrop-blur-xl border border-white/60 rounded-2xl px-5 py-4 shadow-[0_20px_50px_rgba(232,160,191,0.3)]">
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-kay-ink/50">Prix barrés réels</p>
                <p className="font-display text-xl mt-1">Économise jusqu'à <span className="italic text-kay-pink ml-1">25 €</span></p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* EXPLORER PAR UNIVERS — chaque carte ouvre sa page dédiée */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-24" data-testid="universes-section">
        <Reveal className="mb-8">
          <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-kay-pink mb-3">Explorer par univers</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl">
            Découvrir les <span className="italic text-kay-pink">pépites</span>
          </h2>
        </Reveal>
        <UniverseCards products={products} settings={settings} />
      </section>

      {/* MANIFESTO */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28" data-testid="manifesto-section">
        <Reveal>
          <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-kay-pink mb-4">Le manifeste</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl max-w-2xl leading-tight">
            Pourquoi payer le prix plein, <span className="italic text-kay-pink">quand on peut payer le prix doux ?</span>
          </h2>
        </Reveal>
        <div className="mt-14">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.08}>
              <div className="grid md:grid-cols-12 gap-4 items-baseline border-t border-kay-line py-8 group">
                <span className="md:col-span-2 font-mono text-sm text-kay-pink">({c.n})</span>
                <h3 className="md:col-span-4 font-display text-2xl sm:text-3xl group-hover:italic transition-all">{c.title}</h3>
                <p className="md:col-span-6 text-sm sm:text-base text-kay-ink/60 leading-relaxed">{c.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PÉPITES */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-20" data-testid="gems-section">
        <Reveal className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-kay-pink mb-3">Sélection du moment</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl">Les pépites <span className="italic">du moment</span></h2>
          </div>
          <Link to="/boutique" data-testid="gems-see-all" className="group text-sm font-semibold flex items-center gap-2 border-b border-kay-ink pb-1 hover:text-kay-pink hover:border-kay-pink transition-colors">
            Tout voir <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-8">
          {gems.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} onChanged={load} />
          ))}
        </div>
      </section>

      {/* JUSQU'À -50% */}
      <section className="bg-kay-ink text-kay-cream py-20 lg:py-28 grain relative" data-testid="deals-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal className="text-center mb-14">
            <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-kay-rosedeep mb-4">Bonnes affaires</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl">
              Jusqu'à <span className="italic text-kay-rosedeep">-50 %</span>
            </h2>
            <p className="mt-4 text-white/50 text-sm max-w-md mx-auto">
              Prix de référence barrés, économies affichées en euros. La transparence, c'est chic.
            </p>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-8 [&_article]:text-kay-cream [&_h3]:text-kay-cream [&_.add-btn]:bg-kay-rose [&_.add-btn]:text-kay-ink [&_.ref-price]:!text-white/40 [&_.savings-line]:!text-emerald-300">
            {deals.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} onChanged={load} />
            ))}
          </div>
        </div>
      </section>

      <Marquee dark={false} />

      {/* BIENTÔT DISPONIBLES */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-28" data-testid="coming-section">
        <Reveal className="text-center mb-12">
          <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-kay-pink mb-4">Patience…</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl">Bientôt <span className="italic text-kay-pink">disponibles</span></h2>
        </Reveal>
        {soon.length > 0 ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-8">
            {soon.slice(0, 4).map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} onChanged={load} />
            ))}
          </div>
        ) : (
          <Reveal className="text-center">
            <p className="font-display italic text-2xl text-kay-ink/50">De nouvelles pépites arrivent très bientôt…</p>
          </Reveal>
        )}
        <Reveal className="text-center mt-10">
          <Link to="/coming-soon" data-testid="coming-see-all" className="inline-flex items-center gap-2 rounded-full border border-kay-ink px-7 py-3 text-sm font-semibold hover:bg-kay-ink hover:text-white transition-all">
            Être informée des nouveautés <ArrowRight size={15} />
          </Link>
        </Reveal>
      </section>

      {/* CTA IDÉES */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-24" data-testid="ideas-cta">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-kay-rose via-kay-rose to-kay-rosedeep/60 border border-white px-8 py-16 lg:py-20 text-center grain">
            <p className="font-mono text-[11px] tracking-[0.35em] uppercase text-kay-wine/70 mb-4">Ta voix compte</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl max-w-2xl mx-auto leading-tight">
              Une marque que tu rêves de voir <span className="italic text-kay-wine">à petit prix ?</span>
            </h2>
            <Link
              to="/idees"
              data-testid="ideas-cta-button"
              className="mt-8 inline-flex items-center gap-3 rounded-full bg-kay-ink text-white px-8 py-4 text-sm font-semibold hover:bg-kay-wine transition-colors"
            >
              Proposer une idée <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
